import React, { Component } from 'react';

import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-easybutton';
import * as esri from 'esri-leaflet';
import * as jsts from 'jsts';

import Immutable from 'seamless-immutable';

import * as _ from 'lodash';
import { LeafletContainer } from './map.style';

import {
	COLOR_MUNICIPIOS,
	COLOR_MUNICIPIOS_SELECIONADOS,
	URL_MUNICIPIO_GEO,
	CENTROIDE_MAPA,
	ZOOM_INICIAL_MAPA,
	LEAFLET_DRAW_LOCALE,
	PRECISION,
	ZOOM_MAXIMO_MAPA,
	ZOOM_MINIMO_MAPA,
} from '../../constants';

import ArrayHelper from '../../utils/arrayHelper';

class MapGeo extends Component {
	diffMunicipios = ArrayHelper.difference(this.isMunicipioEquals);

	isMunicipioEquals = (municipio1, municipio2) => municipio1.codigoIbge === municipio2.codigoIbge;

	componentDidMount = () => {
		this.map = L.map('map')
			.setView(CENTROIDE_MAPA, ZOOM_INICIAL_MAPA)
			.setMaxZoom(ZOOM_MAXIMO_MAPA)
			.setMinZoom(ZOOM_MINIMO_MAPA);

		this.initMapLayers();

		L.easyButton('fa-crosshairs', () => {
			this.map.setView(CENTROIDE_MAPA, ZOOM_INICIAL_MAPA);
		}, 'Centralizar mapa').addTo(this.map);

		this.map.addLayer(this.editableLayers);

		if (!this.props.isVisualizar) {
			this.insertLeafletDraw(this.map);
		}
	};

	initMapLayers = () => {
		const { geoCadastrados } = this.props;
		const baseMaps = {
			'ESRI Imagery': esri.basemapLayer('Imagery').addTo(this.map),
			'World Street Map': esri.basemapLayer('Streets').addTo(this.map),
		};

		const geo = _.cloneDeep(Immutable.asMutable(geoCadastrados));

		this.municipiosMapAdded = geo && !_.isEmpty(geo) ? geo.municipios : [];

		this.municipioLayer = esri.featureLayer({
			url: URL_MUNICIPIO_GEO,
			style: () => ({ color: COLOR_MUNICIPIOS, weight: 2, fill: false }),
		}).addTo(this.map);

		this.municipiosSelecionados = L.featureGroup().addTo(this.map);
		this.editableLayers = new L.FeatureGroup();

		const overlayMaps = {
			Municipio: this.municipioLayer,
			'Municípios selecionados': this.municipiosSelecionados,
			'Geometrias desenhadas': this.editableLayers,
		};

		L.control.layers(baseMaps, overlayMaps).addTo(this.map);

		if (this.municipiosMapAdded.length !== 0) {
			this.addMunicipiosToMap(this.municipiosMapAdded);
		}

		if (geo && !_.isEmpty(geo.areaDesenhada)) {
			this.addDrawedPolygonsToMap(geo.areaDesenhada);
		}
	}

	addDrawedPolygonsToMap = (geojson) => {
		const geo = L.geoJSON(JSON.parse(geojson));

		this.addNonGroupLayers(geo, this.editableLayers);
	}

	addNonGroupLayers = (sourceLayer, targetGroup) => {
		if (sourceLayer instanceof L.LayerGroup) {
			sourceLayer.eachLayer((layer) => {
				this.addNonGroupLayers(layer, targetGroup);
			});
		} else {
			const municipiosIntersects = [];

			_.each(this.municipiosMapAdded, (m) => {
				if (this.isAIntersectingB(m.geom, sourceLayer.toGeoJSON(PRECISION))) {
					municipiosIntersects.push(m);
				}
			});

			sourceLayer.options.municipiosIntersected = municipiosIntersects;

			targetGroup.addLayer(sourceLayer);
		}
	}

	isAIntersectingB = (a, b) => {
		const GeoJSONReader = new jsts.io.GeoJSONReader();

		const jstsA = GeoJSONReader.read(a);
		const jstsB = GeoJSONReader.read(b);

		return jstsA.intersects(jstsB.geometry);
	}

	insertLeafletDraw = (map) => {
		L.drawLocal = LEAFLET_DRAW_LOCALE;

		map.addLayer(this.editableLayers);

		L.EditToolbar.Delete.include({
			removeAllLayers: false,
		});

		const drawControl = new L.Control.Draw({
			position: 'topleft',
			draw: {
				polyline: false,
				polygon: true,
				circle: false,
				marker: false,
				rectangle: false,
				circlemarker: false,
			},
			edit: {
				featureGroup: this.editableLayers,
				remove: true,
			},
		});
		map.addControl(drawControl);

		this.setDrawEventsOnMap(map);
	};

	setDrawEventsOnMap = (map) => {
		map.on(L.Draw.Event.CREATED, (e) => {
			const { layer } = e;

			this.findMunicipiosToAdd(layer);
		});

		map.on(L.Draw.Event.EDITED, (e) => {
			const { layers } = e;

			if (!layers) {
				return true;
			}

			layers.eachLayer((layer) => {
				_.remove(
					this.municipiosMapAdded,
					m => _.find(layer.options.municipiosIntersected, mi => m.codigoIbge === mi.codigoIbge),
				);
			});

			this.editableLayers.removeLayer(e.layer);
			this.updateProps();

			layers.eachLayer((layer) => {
				this.findMunicipiosToAdd(layer);
			});
		});

		map.on(L.Draw.Event.DELETED, (e) => {
			const { layers } = e;

			if (!layers) {
				return true;
			}

			layers.eachLayer((layer) => {
				_.remove(
					this.municipiosMapAdded,
					m => _.find(layer.options.municipiosIntersected, mi => m.codigoIbge === mi.codigoIbge),
				);
			});

			this.editableLayers.removeLayer(e.layer);
			this.updateProps();
			this.addMunicipiosToMap(this.municipiosMapAdded);
		});
	};

	addMunicipiosToMap = (municipios) => {
		this.municipiosSelecionados.clearLayers();

		_.each(municipios, (municipio) => {
			const layer = L.geoJSON(JSON.parse(municipio.geom));

			layer.bindPopup(
				`<b>${
					municipio.nome
				}</b><br><b>Código IBGE: </b>${
					municipio.codigoIbge}`,
			);

			layer.setStyle({
				color: COLOR_MUNICIPIOS_SELECIONADOS,
				opacity: 0.8,
			});

			this.municipiosSelecionados.addLayer(layer);
		});
		this.editableLayers.bringToFront();
	};

	findMunicipiosToAdd = (layer) => {
		const {
			alertaId, GeoService, updateStateAfterGetIntersection,
		} = this.props;

		GeoService.getResultIntersection(
			layer.toGeoJSON(PRECISION).geometry,
			alertaId,
			updateStateAfterGetIntersection,
			(result) => {
				if (result) {
					if (!this.municipiosMapAdded) {
						this.municipiosMapAdded = [];
					}

					const areaDesenhada = L.geoJSON(JSON.parse(result.areaDesenhada)).getLayers()[0];
					areaDesenhada.options = _.cloneDeep(layer.options);

					if (areaDesenhada.options.municipiosIntersected) {
						_.remove(this.municipiosMapAdded, m => _.find(areaDesenhada.options.municipiosIntersected, mi => m.codigoIbge === mi.codigoIbge));

						areaDesenhada.options.municipiosIntersected = [];
					}

					areaDesenhada.options.municipiosIntersected = result.municipios;

					this.editableLayers.addLayer(areaDesenhada);

					this.editableLayers.removeLayer(layer);

					this.municipiosMapAdded = ArrayHelper.union(
						this.municipiosMapAdded,
						result.municipios,
						this.isMunicipioEquals,
					);

					this.updateProps();
					this.addMunicipiosToMap(this.municipiosMapAdded);
				}
			},
		);
	};

	updateProps = () => {
		const { setAreaDesenhada, setMunicipios } = this.props;

		this.geometryCollection = { type: 'GeometryCollection', geometries: [] };
		this.addToGeometryCollection(this.geometryCollection, this.editableLayers.toGeoJSON(PRECISION));

		setAreaDesenhada(this.geometryCollection);
		setMunicipios(this.municipiosMapAdded);
	}

	addToGeometryCollection = (geometryCollection, feature) => {
		if (feature.type === 'FeatureCollection') {
			for (let j = 0; j < feature.features.length; j++) {
				const featureAtividade = feature.features[j];

				this.addToGeometryCollection(geometryCollection, featureAtividade);
			}
		} else {
			geometryCollection.geometries.push(feature.geometry);
		}
	}

	render = () => <LeafletContainer id='map' />;
}

export default MapGeo;
